// Review & update books, step 2: the lookup chips (Phase 6.3 Part 4; Kristi, Oct 30). What the
// nightly fill-in couldn't settle, per book: Pick the match, Not found, No summary, No cover,
// No Kindle ID and Different from yours. Each answer is remembered in review_dismissals (0042),
// so the same question isn't asked again. No database calls here (tests/logic/lookups.test.ts).

import type { Candidate, Difference } from '../../supabase/functions/_shared/lookup/types.ts';
import type { ShelfBook } from '../shelves/model';

export type { Candidate, Difference };

/** The six lookup chips, in the order they show. */
export type LookupChip = 'match' | 'not_found' | 'summary' | 'cover' | 'kindle' | 'different';
export const LOOKUP_CHIPS: { key: LookupChip; name: string }[] = [
  { key: 'match', name: 'Pick the match' },
  { key: 'not_found', name: 'Not found' },
  { key: 'summary', name: 'No summary' },
  { key: 'cover', name: 'No cover' },
  { key: 'kindle', name: 'No Kindle ID' },
  { key: 'different', name: 'Different from yours' },
];

/** One book's lookup record (work_lookups), as Review reads it. */
export interface LookupRow {
  workId: string;
  result: string;
  needs: string[];
  candidates: Candidate[];
  differences: Difference[];
  lookedUpAt: string;
  /** The book as it is now, read with the record: an answer saved since the lookup (a pasted
   *  Kindle ID, Use this) settles its question even before the next lookup (Kristi, Oct 30). */
  now?: NowFacts;
}

/** What a lookup question is about, as the catalog holds it now. */
export interface NowFacts {
  summary: boolean;
  firstYear: number | null;
  fiction: string | null;
  cover: boolean;
  editions: { id: string; kind: string; asin: string | null; audibleId: string | null; pages: number | null }[];
}

/** The question is settled by what's stored now (the lookup's record is from before the answer). */
export function settledNow(now: NowFacts | undefined, field: string, d?: Difference): boolean {
  if (!now) return false;
  if (d) {
    if (d.field === 'first_published_on') return now.firstYear !== null && String(now.firstYear) === d.found;
    if (d.field === 'fiction') return (now.fiction ?? '').toLowerCase() === d.found.toLowerCase();
    if (d.field === 'pages') return String(now.editions.find((e) => e.id === d.rowId)?.pages ?? '') === d.found;
    return false;
  }
  if (field === 'summary') return now.summary;
  if (field === 'cover_url') return now.cover;
  if (field === 'asin') return now.editions.some((e) => e.kind === 'ebook' && e.asin);
  if (field === 'audible_id') return now.editions.some((e) => e.kind === 'audiobook' && (e.audibleId || e.asin));
  return false;
}

/** An answer you gave, remembered per book and field (review_dismissals). */
export interface Dismissal {
  id: string;
  bookId: string;
  field: string;
  kind: 'keep_mine' | 'leave_blank' | 'keep_as_typed' | 'none_of_these';
  foundValue: string | null;
}

/** One question for you about one book. */
export interface LookupItem {
  chip: LookupChip;
  /** A stable key for the row ("<book>:<chip>:<field>"). */
  key: string;
  book: ShelfBook;
  /** The field it's about, as review_dismissals names it (match, isbn, summary, cover_url, asin, audible_id, or a difference's field). */
  field: string;
  candidates?: Candidate[];
  difference?: Difference;
  /** The book's whole lookup record (a Find one… fill keeps the rest of it as it is). */
  row?: LookupRow;
}

/** A candidate's identity: the record it is on its site. */
export const candidateKey = (c: Candidate): string => (c.openLibraryId ? `ol:${c.openLibraryId}` : c.googleBooksId ? `gb:${c.googleBooksId}` : `url:${c.url}`);

/** "None of these" remembers every candidate turned down (one per line). */
export const noneOfTheseValue = (cands: Candidate[]): string => [...new Set(cands.map(candidateKey))].sort().join('\n');

/** "Keep as typed" remembers the title as it was, so editing it asks again (the author isn't on every list's rows). */
export const typedValue = (b: Pick<ShelfBook, 'title'>): string => b.title;

/** "Keep mine" remembers the value turned down, per edition when it's an edition's ("<row>=<found>", one per line). */
export const keepMineLine = (d: Difference): string => `${d.table === 'editions' ? d.rowId : 'work'}=${d.found}`;
export function keepMineValue(d: Difference, before: string | null): string {
  const lines = (before ?? '').split('\n').filter(Boolean);
  const row = d.table === 'editions' ? d.rowId : 'work';
  return [...lines.filter((l) => !l.startsWith(`${row}=`)), keepMineLine(d)].join('\n');
}

/** The field name a difference is remembered under (letters and _ only: review_dismissals' rule). */
export const differenceField = (d: Difference): string => `diff_${d.field}`.replace(/[^a-z_]/g, '_').slice(0, 60);

const SHELF_ORDER: Record<string, number> = {
  currently_reading: 0,
  up_next: 1,
  future_reads: 2,
  finished: 4,
  not_for_me: 5,
  reference: 6,
};
export const shelfRank = (b: Pick<ShelfBook, 'status'>) => (b.status === null ? 3 : SHELF_ORDER[b.status] ?? 3);

/** Currently Reading and Up Next first (Kristi, Oct 30: "Up Next first"), then in your order, then by title. */
export function byShelf(a: ShelfBook, b: ShelfBook): number {
  return shelfRank(a) - shelfRank(b) || (a.position ?? 1e9) - (b.position ?? 1e9) || a.title.localeCompare(b.title);
}

/** Is this question answered already (and nothing new turned up since)? */
export function dismissed(item: Omit<LookupItem, 'key'>, ds: Map<string, Dismissal>): boolean {
  const d = ds.get(`${item.book.id}:${item.field}`);
  if (!d) return false;
  if (d.kind === 'leave_blank') return true;
  if (d.kind === 'keep_as_typed') return item.field === 'match' ? d.foundValue === typedValue(item.book) : true;
  if (d.kind === 'none_of_these') {
    // Asked again when the 30-day re-check finds a candidate that wasn't turned down (Kristi, Oct 30).
    const seen = new Set((d.foundValue ?? '').split('\n'));
    return (item.candidates ?? []).every((c) => seen.has(candidateKey(c)));
  }
  if (d.kind === 'keep_mine' && item.difference) return (d.foundValue ?? '').split('\n').includes(keepMineLine(item.difference));
  return false;
}

/** Every question for your books, per chip, Up Next first. Books not on your list are left out. */
export function lookupItems(rows: LookupRow[], books: ShelfBook[], dismissals: Dismissal[]): Record<LookupChip, LookupItem[]> {
  const byWork = new Map<string, ShelfBook>();
  for (const b of books) if (!byWork.has(b.workId)) byWork.set(b.workId, b);
  const ds = new Map(dismissals.map((d) => [`${d.bookId}:${d.field}`, d]));
  const out: Record<LookupChip, LookupItem[]> = { match: [], not_found: [], summary: [], cover: [], kindle: [], different: [] };
  let current: LookupRow | undefined;
  const add = (x: Omit<LookupItem, 'key'>) => {
    if (dismissed(x, ds) || settledNow(current?.now, x.field, x.difference)) return;
    x = { ...x, row: current };
    out[x.chip].push({ ...x, key: `${x.book.id}:${x.chip}:${x.field}${x.difference ? `:${x.difference.rowId}` : ''}` });
  };
  for (const r of rows) {
    const book = byWork.get(r.workId);
    if (!book) continue;
    current = r;
    if (r.result === 'choose') add({ chip: 'match', book, field: 'match', candidates: r.candidates });
    else if (r.result === 'not_found') add({ chip: 'not_found', book, field: 'match' });
    if (r.needs.includes('isbn')) add({ chip: 'not_found', book, field: 'isbn' });
    if (r.needs.includes('summary')) add({ chip: 'summary', book, field: 'summary' });
    // A cover of your own (a private link) already answers it.
    if (r.needs.includes('cover_url') && !book.cover) add({ chip: 'cover', book, field: 'cover_url' });
    for (const f of ['asin', 'audible_id'] as const) if (r.needs.includes(f)) add({ chip: 'kindle', book, field: f });
    for (const d of r.differences) add({ chip: 'different', book, field: differenceField(d), difference: d });
  }
  for (const k of Object.keys(out) as LookupChip[]) out[k].sort((a, b) => byShelf(a.book, b.book));
  return out;
}

/** The books with any lookup question, for the Review & update books [N] count. */
export function lookupBookIds(items: Record<LookupChip, LookupItem[]>): Set<string> {
  return new Set(Object.values(items).flatMap((xs) => xs.map((x) => x.book.id)));
}

/** A Google Drive share link turned into the picture's own address (Kristi, Oct 30); anything else as it is. */
export function coverLink(raw: string): string {
  const s = raw.trim();
  const m =
    s.match(/^https:\/\/drive\.google\.com\/file\/d\/([A-Za-z0-9_-]{10,})/) ??
    s.match(/^https:\/\/drive\.google\.com\/(?:open|uc)\?(?:.*&)?id=([A-Za-z0-9_-]{10,})/);
  return m ? `https://drive.google.com/thumbnail?id=${m[1]}&sz=w1000` : s;
}

/**
 * Your own cover link as a picture address: a Drive share link becomes its picture; any other
 * Drive or Docs page (not a picture) is skipped, so the catalog's cover shows instead.
 */
export function ownCover(url: string | null | undefined): string | null {
  if (!url) return null;
  const u = coverLink(url);
  return /^https:\/\/(drive|docs)\.google\.com\//.test(u) && !/^https:\/\/drive\.google\.com\/thumbnail\?/.test(u) ? null : u;
}

/** A Kindle or Audible ID from a pasted ID or store link (amazon.com/dp/…, /gp/product/…, audible.com/pd/…/B0…). */
export function storeIdIn(raw: string): string | null {
  const s = raw.trim();
  if (/^[0-9A-Z]{10}$/i.test(s)) return s.toUpperCase();
  const m = s.match(/\/(?:dp|gp\/product|pd(?:\/[^/?#]+)?)\/([0-9A-Z]{10})(?:[/?#]|$)/i);
  return m ? m[1]!.toUpperCase() : null;
}

/**
 * The store ID in a pasted Amazon or Audible link, and which kind it is: an Audible page (on
 * audible.com, or Amazon's /audible/ pages) gives an Audible ID, any other Amazon page a Kindle ID.
 * Used when the search finds nothing, so the ID you found isn't lost (Kristi, Oct 30).
 */
export function linkStoreId(raw: string): { field: 'asin' | 'audible_id'; id: string } | null {
  const s = raw.trim();
  if (!/^https?:\/\/(www\.)?(amazon|audible)\./i.test(s)) return null;
  const m = s.match(/\/(B[0-9A-Z]{9})(?:[/?#]|$)/i);
  if (!m) return null;
  return { field: /audible/i.test(s) ? 'audible_id' : 'asin', id: m[1]!.toUpperCase() };
}

/** A difference in words: what's yours, what was found, and where. */
export function differenceText(d: Difference): { label: string; yours: string; found: string } {
  const label =
    d.field === 'first_published_on'
      ? 'First published'
      : d.field === 'fiction'
        ? 'Literary genre'
        : d.field === 'pages'
          ? `Pages${d.edition ? ` (${d.edition})` : ''}`
          : d.field === 'isbn13'
            ? `ISBN${d.edition ? ` (${d.edition})` : ''}`
            : d.field;
  return { label, yours: d.yours, found: d.found };
}

/** Whether Use this can save the found value (an ISBN "not among Open Library's" has no value to use). */
export const canUse = (d: Difference): boolean => d.field !== 'isbn13' && /\S/.test(d.found);
